import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { adminClubsAPI } from '../services/adminApi';
import { useAdminAuth } from '../contexts/AdminAuthContext';

const PAGE_SIZE = 20;

const WEEKDAYS = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];

const Container = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  max-width: 960px;
`;

const Title = styled.h2`
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
`;

const Subtitle = styled.p`
  margin: 0 0 1.5rem 0;
  color: #6b7280;
  font-size: 0.875rem;
`;

const FormRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
`;

const Toolbar = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 1rem;
`;

const Input = styled.input`
  flex: 1;
  min-width: 160px;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 64px;
  padding: 0.65rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  font-family: inherit;
`;

const Btn = styled.button`
  padding: 0.65rem 1rem;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  border: ${(p) => (p.$primary ? 'none' : '1px solid #cbd5e1')};
  background: ${(p) => (p.$primary ? '#043663' : '#fff')};
  color: ${(p) => (p.$primary ? '#fff' : '#043663')};
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
  th,
  td {
    text-align: left;
    padding: 0.65rem 0.5rem;
    border-bottom: 1px solid #e5e7eb;
  }
  th {
    color: #6b7280;
    font-weight: 600;
    font-size: 0.75rem;
    text-transform: uppercase;
  }
`;

const Pager = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 1rem;
  font-size: 0.85rem;
  color: #6b7280;
`;

const Err = styled.div`
  color: #b91c1c;
  font-size: 0.85rem;
  margin-bottom: 0.75rem;
`;

const Msg = styled.div`
  color: #166534;
  font-size: 0.85rem;
  margin-bottom: 0.75rem;
`;

const Badge = styled.span`
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.15rem 0.45rem;
  border-radius: 999px;
  background: ${(p) => (p.$on ? '#dcfce7' : '#f3f4f6')};
  color: ${(p) => (p.$on ? '#166534' : '#6b7280')};
`;

const EditPanel = styled.div`
  margin: 1rem 0 1.5rem;
  padding: 1.25rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  input,
  textarea {
    text-transform: none;
    font-weight: 400;
    letter-spacing: normal;
    color: #0f172a;
  }
`;

const Help = styled.p`
  margin: 0 0 1rem;
  font-size: 0.8rem;
  color: #64748b;
  line-height: 1.4;
`;

const HoursRow = styled.div`
  display: grid;
  grid-template-columns: 48px 1fr 1fr;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 0.35rem;
  font-size: 0.8rem;
  color: #334155;
`;

function fold(s) {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function emptyHours() {
  const o = {};
  WEEKDAYS.forEach(({ key }) => {
    o[key] = { open: '', close: '' };
  });
  return o;
}

function hoursFromClub(openingHours) {
  const base = emptyHours();
  if (!openingHours || typeof openingHours !== 'object') return base;
  WEEKDAYS.forEach(({ key }) => {
    const slot = openingHours[key];
    if (slot) {
      base[key] = {
        open: slot.open || '',
        close: slot.close || '',
      };
    }
  });
  return base;
}

function hoursToPayload(hoursState) {
  const out = {};
  let any = false;
  WEEKDAYS.forEach(({ key }) => {
    const open = String(hoursState[key]?.open || '').trim();
    const close = String(hoursState[key]?.close || '').trim();
    if (open && close) {
      out[key] = { open, close };
      any = true;
    }
  });
  return any ? out : null;
}

function peakWindowsToText(windows) {
  if (!Array.isArray(windows) || !windows.length) return '';
  return windows
    .map((w) => {
      const days = Array.isArray(w.days) ? w.days.join(',') : '';
      return `${days}|${w.start || ''}|${w.end || ''}`;
    })
    .join('\n');
}

/** lines: "1,2,3,4,5|17:00|21:00" days 0=Sun..6=Sat */
function textToPeakWindows(text) {
  const lines = String(text || '')
    .split(/\n+/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (!lines.length) return null;
  return lines.map((line) => {
    const [daysPart, start, end] = line.split('|').map((p) => p.trim());
    const days = (daysPart || '')
      .split(/[, ]+/)
      .map((d) => Number(d))
      .filter((n) => !Number.isNaN(n) && n >= 0 && n <= 6);
    return { days, start: start || '', end: end || '' };
  });
}

function clubToEditState(c) {
  return {
    name: c.name || '',
    canton: c.canton || '',
    isPartner: !!c.isPartner,
    courtCount: c.courtCount != null ? String(c.courtCount) : '',
    slotDurationHours: c.slotDurationHours != null ? String(c.slotDurationHours) : '1.5',
    hourlyRateChf: c.hourlyRateChf != null ? String(c.hourlyRateChf) : '',
    nameAliases: Array.isArray(c.nameAliases) ? c.nameAliases.join(', ') : '',
    openingHours: hoursFromClub(c.openingHours),
    peakWindowsText: peakWindowsToText(c.peakWindows),
  };
}

export default function AdminSettingsClubs() {
  const { adminUser } = useAdminAuth();
  const canEdit =
    adminUser?.isSuperAdmin ||
    adminUser?.is_super_admin ||
    adminUser?.permissions?.settings_general?.edit;
  const queryClient = useQueryClient();
  const [newName, setNewName] = useState('');
  const [newCanton, setNewCanton] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [edit, setEdit] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading, isError, error: queryError } = useQuery(
    ['admin-clubs', showInactive],
    () => adminClubsAPI.list({ includeInactive: showInactive }),
    { keepPreviousData: true }
  );

  const clubs = Array.isArray(data?.data)
    ? data.data
    : Array.isArray(data)
      ? data
      : [];

  const filtered = useMemo(() => {
    const q = fold(search);
    if (!q) return clubs;
    return clubs.filter(
      (c) => fold(c.name).includes(q) || fold(c.canton).includes(q)
    );
  }, [clubs, search]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSafe = Math.min(page, pageCount);
  const pageRows = useMemo(() => {
    const start = (pageSafe - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, pageSafe]);

  const invalidate = () => {
    queryClient.invalidateQueries('admin-clubs');
    queryClient.invalidateQueries('clubs-catalog');
  };

  const createMut = useMutation((payload) => adminClubsAPI.create(payload), {
    onSuccess: () => {
      setNewName('');
      setNewCanton('');
      setMessage('Club added');
      setError('');
      setSearch('');
      setPage(1);
      invalidate();
    },
    onError: (err) => {
      setError(err?.response?.data?.message || err.message || 'Failed to add club');
      setMessage('');
    },
  });

  const updateMut = useMutation(
    ({ id, ...payload }) => adminClubsAPI.update(id, payload),
    {
      onSuccess: () => {
        setEditingId(null);
        setEdit(null);
        setMessage('Club updated');
        setError('');
        invalidate();
      },
      onError: (err) => {
        setError(err?.response?.data?.message || err.message || 'Failed to update club');
        setMessage('');
      },
    }
  );

  const deleteMut = useMutation((id) => adminClubsAPI.remove(id), {
    onSuccess: () => {
      setMessage('Club removed from dropdown');
      setError('');
      invalidate();
    },
    onError: (err) => {
      setError(err?.response?.data?.message || err.message || 'Failed to remove club');
      setMessage('');
    },
  });

  const startEdit = (c) => {
    setEditingId(c.id);
    setEdit(clubToEditState(c));
    setError('');
    setMessage('');
  };

  const saveEdit = () => {
    if (!editingId || !edit) return;
    updateMut.mutate({
      id: editingId,
      name: edit.name.trim(),
      canton: edit.canton.trim(),
      isActive: true,
      isPartner: !!edit.isPartner,
      courtCount: edit.courtCount === '' ? null : Number(edit.courtCount),
      slotDurationHours: edit.slotDurationHours === '' ? 1.5 : Number(edit.slotDurationHours),
      hourlyRateChf: edit.hourlyRateChf === '' ? null : Number(edit.hourlyRateChf),
      nameAliases: edit.nameAliases,
      openingHours: hoursToPayload(edit.openingHours),
      peakWindows: textToPeakWindows(edit.peakWindowsText),
    });
  };

  return (
    <Container>
      <Title>Clubs (match venue list)</Title>
      <Subtitle>
        These names appear when teams add match details. Partner report variables (rate, hours, peak
        windows) live here — leave blank and those analytics metrics show as N/A.
      </Subtitle>

      {error ? <Err>{error}</Err> : null}
      {message ? <Msg>{message}</Msg> : null}
      {isError ? (
        <Err>
          Failed to load clubs:{' '}
          {queryError?.response?.data?.message || queryError?.message || 'Unknown error'}
        </Err>
      ) : null}

      {canEdit ? (
        <FormRow>
          <Input
            placeholder="New club name…"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newName.trim()) {
                createMut.mutate({
                  name: newName.trim(),
                  canton: newCanton.trim() || undefined,
                });
              }
            }}
          />
          <Input
            placeholder="Canton (GE, ZH…)"
            value={newCanton}
            onChange={(e) => setNewCanton(e.target.value)}
            style={{ maxWidth: '120px', flex: '0 0 120px' }}
          />
          <Btn
            type="button"
            $primary
            disabled={!newName.trim() || createMut.isLoading}
            onClick={() =>
              createMut.mutate({
                name: newName.trim(),
                canton: newCanton.trim() || undefined,
              })
            }
          >
            Add club
          </Btn>
        </FormRow>
      ) : null}

      {editingId && edit && canEdit ? (
        <EditPanel>
          <Title style={{ fontSize: '1.1rem' }}>Edit club report settings</Title>
          <Help>
            Hours = matches × slot duration. Revenue needs hourly rate. Peak % needs peak windows.
            Occupancy needs court count + opening hours. Aliases match alternate spellings in match
            details.
          </Help>
          <FieldGrid>
            <Field>
              Name
              <Input
                value={edit.name}
                onChange={(e) => setEdit({ ...edit, name: e.target.value })}
              />
            </Field>
            <Field>
              Canton
              <Input
                value={edit.canton}
                onChange={(e) => setEdit({ ...edit, canton: e.target.value })}
                placeholder="GE"
              />
            </Field>
            <Field>
              Courts
              <Input
                type="number"
                min="0"
                value={edit.courtCount}
                onChange={(e) => setEdit({ ...edit, courtCount: e.target.value })}
                placeholder="e.g. 4"
              />
            </Field>
            <Field>
              Slot hours / match
              <Input
                type="number"
                min="0.25"
                step="0.25"
                value={edit.slotDurationHours}
                onChange={(e) => setEdit({ ...edit, slotDurationHours: e.target.value })}
              />
            </Field>
            <Field>
              Hourly rate (CHF)
              <Input
                type="number"
                min="0"
                step="1"
                value={edit.hourlyRateChf}
                onChange={(e) => setEdit({ ...edit, hourlyRateChf: e.target.value })}
                placeholder="Leave blank = N/A"
              />
            </Field>
            <Field style={{ justifyContent: 'flex-end' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1.4rem' }}>
                <input
                  type="checkbox"
                  checked={edit.isPartner}
                  onChange={(e) => setEdit({ ...edit, isPartner: e.target.checked })}
                />
                <span style={{ textTransform: 'none', fontWeight: 600, color: '#0f172a' }}>
                  Partner club (benchmark list)
                </span>
              </span>
            </Field>
          </FieldGrid>

          <Field style={{ marginBottom: '0.75rem' }}>
            Name aliases (comma-separated)
            <Input
              value={edit.nameAliases}
              onChange={(e) => setEdit({ ...edit, nameAliases: e.target.value })}
              placeholder="Padel Club Zurich, PCZ…"
            />
          </Field>

          <Field style={{ marginBottom: '0.75rem' }}>
            Opening hours (leave empty = closed that day)
            {WEEKDAYS.map(({ key, label }) => (
              <HoursRow key={key}>
                <span>{label}</span>
                <Input
                  type="time"
                  value={edit.openingHours[key]?.open || ''}
                  onChange={(e) =>
                    setEdit({
                      ...edit,
                      openingHours: {
                        ...edit.openingHours,
                        [key]: { ...edit.openingHours[key], open: e.target.value },
                      },
                    })
                  }
                />
                <Input
                  type="time"
                  value={edit.openingHours[key]?.close || ''}
                  onChange={(e) =>
                    setEdit({
                      ...edit,
                      openingHours: {
                        ...edit.openingHours,
                        [key]: { ...edit.openingHours[key], close: e.target.value },
                      },
                    })
                  }
                />
              </HoursRow>
            ))}
          </Field>

          <Field style={{ marginBottom: '1rem' }}>
            Peak windows (one per line: days|start|end — days 0=Sun … 6=Sat)
            <TextArea
              value={edit.peakWindowsText}
              onChange={(e) => setEdit({ ...edit, peakWindowsText: e.target.value })}
              placeholder={'1,2,3,4,5|17:00|21:00\n0,6|09:00|18:00'}
            />
          </Field>

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Btn type="button" $primary disabled={!edit.name.trim() || updateMut.isLoading} onClick={saveEdit}>
              {updateMut.isLoading ? 'Saving…' : 'Save club'}
            </Btn>
            <Btn
              type="button"
              onClick={() => {
                setEditingId(null);
                setEdit(null);
              }}
            >
              Cancel
            </Btn>
          </div>
        </EditPanel>
      ) : null}

      <Toolbar>
        <Input
          type="search"
          placeholder="Search club name or canton…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          aria-label="Search clubs"
        />
        <label
          style={{
            display: 'flex',
            gap: '0.5rem',
            alignItems: 'center',
            fontSize: '0.85rem',
            whiteSpace: 'nowrap',
          }}
        >
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => {
              setShowInactive(e.target.checked);
              setPage(1);
            }}
          />
          Show removed
        </label>
      </Toolbar>

      <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.75rem' }}>
        {isLoading
          ? 'Loading…'
          : `${filtered.length} club${filtered.length === 1 ? '' : 's'}${
              search.trim() ? ' match' : ''
            }${clubs.length !== filtered.length ? ` (of ${clubs.length})` : ''}`}
      </div>

      <Table>
        <thead>
          <tr>
            <th>#</th>
            <th>Name</th>
            <th>Canton</th>
            <th>Partner</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {pageRows.length === 0 && !isLoading && !isError ? (
            <tr>
              <td colSpan={6} style={{ color: '#6b7280' }}>
                {search.trim()
                  ? 'No clubs match this search.'
                  : 'No clubs for this tenant yet. Add clubs above or re-run the seed migration.'}
              </td>
            </tr>
          ) : null}
          {pageRows.map((c, idx) => {
            const sr = (pageSafe - 1) * PAGE_SIZE + idx + 1;
            return (
              <tr key={c.id} style={editingId === c.id ? { background: '#eff6ff' } : undefined}>
                <td>{sr}</td>
                <td>{c.name}</td>
                <td>{c.canton || '—'}</td>
                <td>
                  <Badge $on={!!c.isPartner}>{c.isPartner ? 'Partner' : '—'}</Badge>
                </td>
                <td>
                  <Badge $on={c.isActive}>{c.isActive ? 'Active' : 'Removed'}</Badge>
                </td>
                <td style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                  {canEdit && c.isActive ? (
                    <>
                      <Btn type="button" onClick={() => startEdit(c)}>
                        Edit
                      </Btn>
                      <Btn
                        type="button"
                        disabled={deleteMut.isLoading}
                        onClick={() => {
                          if (window.confirm(`Remove “${c.name}” from the dropdown?`)) {
                            deleteMut.mutate(c.id);
                          }
                        }}
                      >
                        Remove
                      </Btn>
                    </>
                  ) : null}
                  {canEdit && !c.isActive ? (
                    <Btn
                      type="button"
                      $primary
                      onClick={() => updateMut.mutate({ id: c.id, isActive: true })}
                    >
                      Restore
                    </Btn>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      {filtered.length > 0 ? (
        <Pager>
          <span>
            Page {pageSafe} / {pageCount}
          </span>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Btn type="button" disabled={pageSafe <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Previous
            </Btn>
            <Btn
              type="button"
              disabled={pageSafe >= pageCount}
              onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
            >
              Next
            </Btn>
          </div>
        </Pager>
      ) : null}
    </Container>
  );
}
